import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Title3,
  Title4,
  Title5,
  TextLarge,
  Card,
  SecondaryButton,
  UtilsBlock,
  Image,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  PrimaryIconButton,
  Link,
} from '@hz-design-system/web-ui';
import useEmblaCarousel from 'embla-carousel-react';
import carouselCclassNames from 'embla-carousel-class-names';

import Classes from './Copy.module.scss';
import getStaticImageUrl from '../../../utils/getStaticImageUrl';
import ruleIcon1 from '../../../../images/landing-page/overdrive/icons/mp-icon-jezelf.svg';
import ruleIcon2 from '../../../../images/landing-page/overdrive/icons/mp-icon-auto.svg';
import ruleIcon3 from '../../../../images/landing-page/overdrive/icons/mp-agreement-icon.svg';
import step1 from '../../../../images/landing-page/overdrive/MP-overdrive-step-1.svg';
import step2 from '../../../../images/landing-page/overdrive/MP-overdrive-step-2.svg';
import step3 from '../../../../images/landing-page/overdrive/MP-overdrive-step-3.svg';
import step4 from '../../../../images/landing-page/overdrive/MP-overdrive-step-4.svg';
import step5 from '../../../../images/landing-page/overdrive/MP-overdrive-step-5.svg';

import { TEnvConfig } from 'types/TServerContext';

type TParams = {
  environment: TEnvConfig;
};

const Copy = ({ environment }: TParams) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [carouselCclassNames()]);
  const feedButtonRef = useRef<HTMLDivElement | null>(null);
  const [isFeedButtonSticky, setIsFeedButtonSticky] = useState(true);
  const { staticBaseUrl } = environment;

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    const elementRef = feedButtonRef.current as Element;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsFeedButtonSticky(!entry.isIntersecting && entry.boundingClientRect.top > 0);
        });
      },
      {
        threshold: 1,
      },
    );

    if (feedButtonRef.current) {
      observer.observe(elementRef);
    }

    return () => {
      if (elementRef) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <>
      <div className={Classes.steps} id="claim-je-korting">
        <Title3 utilities={['marginBottomL']} className={Classes.stepsTitle}>
          Zo claim je de korting:
        </Title3>
        <div className={Classes.stepCards}>
          <div ref={emblaRef} className={Classes.cover}>
            <div className={Classes.scroll}>
              <div className={Classes.scrollItem}>
                <Card variant="filled" className={Classes.stepCard}>
                  <Image
                    aspectRatio="landscape"
                    className={Classes.stepImage}
                    alt="Illustratie van stap 1: vind je auto"
                    src={getStaticImageUrl(staticBaseUrl, step1)}
                  />
                  <div className={Classes.stepTitle}>
                    <span className={Classes.stepTitleCount}>1</span>
                    Vind je auto
                  </div>
                  <TextLarge as="p" utilities={['marginTopS']}>
                    Vind jouw favoriete elektrische occasion op Marktplaats en lees
                    <Link href="#voorwaarden"> alle voorwaarden</Link> van de EV-kortingsactie goed door. Check meteen
                    deze pagina of er nog budget beschikbaar is, want op=op.
                  </TextLarge>
                </Card>
              </div>
              <div className={Classes.scrollItem}>
                <Card variant="filled" className={Classes.stepCard}>
                  <Image
                    aspectRatio="landscape"
                    className={Classes.stepImage}
                    alt="Illustratie van stap 2: neem contact op"
                    src={getStaticImageUrl(staticBaseUrl, step2)}
                  />
                  <div className={Classes.stepTitle}>
                    <span className={Classes.stepTitleCount}>2</span>
                    Neem contact op
                  </div>
                  <TextLarge as="p" utilities={['marginTopS']}>
                    Neem zo snel mogelijk contact op met het autobedrijf of ga langs in de showroom.
                  </TextLarge>
                </Card>
              </div>
              <div className={Classes.scrollItem}>
                <Card variant="filled" className={Classes.stepCard}>
                  <Image
                    aspectRatio="landscape"
                    className={Classes.stepImage}
                    alt="Illustratie van stap 3: geef je gegevens door"
                    src={getStaticImageUrl(staticBaseUrl, step3)}
                  />
                  <div className={Classes.stepTitle}>
                    <span className={Classes.stepTitleCount}>3</span>
                    Geef je gegevens door
                  </div>
                  <TextLarge as="p" utilities={['marginTopS']}>
                    Geef je gegevens door aan de verkoper van het autobedrijf, hij of zij controleert direct bij
                    Marktplaats of jij en de auto in aanmerking komen voor de €1.000 korting.
                  </TextLarge>
                </Card>
              </div>
              <div className={Classes.scrollItem}>
                <Card variant="filled" className={Classes.stepCard}>
                  <Image
                    aspectRatio="landscape"
                    className={Classes.stepImage}
                    alt="Illustratie van stap 4: sluit de overeenkomst"
                    src={getStaticImageUrl(staticBaseUrl, step4)}
                  />
                  <div className={Classes.stepTitle}>
                    <span className={Classes.stepTitleCount}>4</span>
                    Sluit de overeenkomst
                  </div>
                  <TextLarge as="p" utilities={['marginTopS']}>
                    De verkoper hoort direct van Marktplaats of de €1.000 korting gegeven kan worden. De korting wordt
                    vervolgens door het autobedrijf op je factuur vermeld, en sluit je de koopovereenkomst met het
                    bedrijf.
                  </TextLarge>
                </Card>
              </div>
              <div className={Classes.scrollItem}>
                <Card variant="filled" className={Classes.stepCard}>
                  <Image
                    aspectRatio="landscape"
                    className={Classes.stepImage}
                    alt="Illustratie van stap 5: rond de verkoop af"
                    src={getStaticImageUrl(staticBaseUrl, step5)}
                  />
                  <div className={Classes.stepTitle}>
                    <span className={Classes.stepTitleCount}>5</span>
                    Rond de verkoop af
                  </div>
                  <TextLarge as="p" utilities={['marginTopS']}>
                    De auto wordt op jouw naam in het kentekenregister gezet. Daarmee is de verkoop definitief afgerond.
                    Veel rijplezier!
                  </TextLarge>
                </Card>
              </div>
            </div>
          </div>
          <PrimaryIconButton
            className={`${Classes.stepButton} ${Classes.stepButtonPrev}`}
            name="arrow-left-regular"
            onClick={scrollPrev}
            // onClick={() => onClick(stepIndex - 1)}
          />
          <PrimaryIconButton
            className={`${Classes.stepButton} ${Classes.stepButtonNext}`}
            name="arrow-right-regular"
            onClick={scrollNext}
            // onClick={() => onClick(stepIndex + 1)}
          />
        </div>
      </div>
      <div className={Classes.rules} id="voorwaarden">
        <Title3 as="h4" utilities={['marginBottomXxl']}>
          Dit zijn de voorwaarden
        </Title3>
        <div className={Classes.ruleCards}>
          <Card variant="filled" className={Classes.ruleCard}>
            <Image
              className={Classes.ruleIcon}
              alt="Icoon van jezelf"
              src={getStaticImageUrl(staticBaseUrl, ruleIcon1)}
            />
            <Title5 as="div">Over jezelf</Title5>
            <ul className={Classes.list}>
              <li className={Classes.ruleBullet}>bent een particulier;</li>
              <li className={Classes.ruleBullet}>hebt een geldig woonadres in Nederland;</li>
              <li className={Classes.ruleBullet}>hebt niet eerder gebruik gemaakt van deze Marktplaats EV-korting.</li>
            </ul>
          </Card>
          <Card variant="filled" className={Classes.ruleCard}>
            <Image
              className={Classes.ruleIcon}
              alt="Icoon van de auto"
              src={getStaticImageUrl(staticBaseUrl, ruleIcon2)}
            />
            <Title5 as="div">Over de auto</Title5>
            <ul className={Classes.list}>
              <li className={Classes.ruleBullet}>
                staat te koop op Marktplaats, bij een autobedrijf dat in Nederland is gevestigd;
              </li>
              <li className={Classes.ruleBullet}>
                is een 100% elektrische personenauto (dus g&eacute;&eacute;n ombouw of hybride);
              </li>
              <li className={Classes.ruleBullet}>heeft een actieradius van minimaal 120 km;</li>
              <li className={Classes.ruleBullet}>heeft een kilometerstand van 5.000 km of hoger;</li>
              <li className={Classes.ruleBullet}>
                heeft een vraagprijs op Marktplaats tussen de €15.000 en €35.000, incl. btw.
              </li>
            </ul>
          </Card>
          <Card variant="filled" className={Classes.ruleCard}>
            <Image
              className={Classes.ruleIcon}
              alt="Icoon van overeenkomst"
              src={getStaticImageUrl(staticBaseUrl, ruleIcon3)}
            />
            <Title5 as="div">Over de overeenkomst</Title5>
            <ul className={Classes.list}>
              <li className={Classes.ruleBullet}>staat op jouw naam</li>
              <li className={Classes.ruleBullet}>
                gaat om een koop (dus geen financial of private lease via een andere partij);
              </li>
              <li className={Classes.ruleBullet}>
                bevat een specifieke vermelding van de €1.000 Marktplaats - EV korting;
              </li>
              <li className={Classes.ruleBullet}>bevat het kenteken van de auto.</li>
            </ul>
          </Card>
        </div>
      </div>
      <div className={Classes.terms}>
        <Title4 as="h5" utilities={['marginBottomL']}>
          Overige voorwaarden
        </Title4>
        <div>
          <TextLarge as="div" utilities={['marginBottomL']}>
            Ook deze kleine lettertjes zijn belangrijk voor deelname aan de actie
          </TextLarge>
        </div>
        <div className={Classes.termsToggle}>
          <Accordion className={Classes.allTerms}>
            <AccordionItem>
              <AccordionHeader>Bekijk hier de overige voorwaarden</AccordionHeader>
              <AccordionBody>
                <ul className={Classes.list}>
                  <li className={Classes.ruleBullet}>
                    De auto is niet vóór 6 januari 2026 op jouw naam geregistreerd bij het RDW;
                  </li>
                  <li className={Classes.ruleBullet}>
                    De auto heeft niet eerder op naam gestaan van iemand op hetzelfde woonadres;
                  </li>
                  <li className={Classes.ruleBullet}>
                    Indien er geen vraagprijs gecommuniceerd staat online, dan wordt gekeken naar de prijs op de
                    koopovereenkomst. De prijs moet dan tussen de €15.000 en €35.000, incl. btw liggen, om aanspraak te
                    kunnen maken op de €1.000 Marktplaats EV-korting.
                  </li>
                  <li className={Classes.ruleBullet}>
                    De auto wordt na levering overgeschreven. De auto hoeft nog niet geleverd te zijn bij aanvraag van
                    de korting, maar tenaamstelling moet binnen 30 dagen gebeuren.
                  </li>
                  <li className={Classes.ruleBullet}>
                    De overeenkomst is afgesloten met een in Nederland gevestigd autobedrijf dat adverteert op
                    Marktplaats;
                  </li>
                  <li className={Classes.ruleBullet}>
                    De overeenkomst is ondertekend door jou én het autobedrijf, op papier of digitaal;
                  </li>
                  <li className={Classes.ruleBullet}>
                    Om te controleren of je aan de voorwaarden voldoet, moet het autobedrijf jouw persoonsgegevens met
                    Marktplaats delen. Zonder die toestemming kun je geen gebruikmaken van de €1.000 Marktplaats
                    EV-korting. Het gaat om je naam, adres, woonplaats, e-mailadres en telefoonnummer.
                  </li>
                  <li className={Classes.ruleBullet}>
                    Je ontvangt de Marktplaats-korting via het autobedrijf. Marktplaats betaalt €1.000 aan het
                    autobedrijf, dat dit bedrag vervolgens verrekent in jouw koopovereenkomst of factuur. Je kunt de
                    €1.000 Marktplaats EV-korting dus niet rechtstreeks bij Marktplaats opvragen of verhalen.
                  </li>
                  <li className={Classes.ruleBullet}>
                    Marktplaats behoudt zich het recht voor om de actie op elk moment te beëindigen.
                  </li>
                  <li className={Classes.ruleBullet}>
                    Het autobedrijf kan de €1.000 Marktplaats EV-korting aanvragen zolang er budget beschikbaar is. Dit
                    wordt altijd telefonisch bevestigd door een servicemedewerker van Marktplaats.
                  </li>
                  <li className={Classes.ruleBullet}>
                    Is het budget op? Dan is het autobedrijf niet verplicht om alsnog €1.000 Marktplaats EV-korting te
                    geven.
                  </li>
                  <li className={Classes.ruleBullet}>
                    De
                    <Link href="https://www.marktplaats.nl/i/help/over-marktplaats/voorwaarden-en-privacybeleid/algemene-gebruiksvoorwaarden.dot.html">
                      {` Gebruiksvoorwaarden van Marktplaats `}
                    </Link>
                    zijn van toepassing op deze actie.
                  </li>
                </ul>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <div className={Classes.searchButtonSection}>
        <Title4 as="div" className={Classes.searchTitle1}>
          Wees er
        </Title4>
        <Title4 as="div" className={Classes.searchTitle2}>
          snel bij
        </Title4>
        <UtilsBlock as="p" color="text-primary" my="s">
          We hopen je een lekker voordeeltje te bezorgen met deze actie! Hou wel het budget goed in de gaten, want
          op=op.
        </UtilsBlock>
        <div ref={feedButtonRef} className={Classes.searchButtonPlaceHolder}>
          <div className={`${Classes.searchButton} ${isFeedButtonSticky ? Classes.stickySearchButton : ''}`}>
            <SecondaryButton href="#het-aanbod" as="a">
              Bekijk het aanbod
            </SecondaryButton>
          </div>
        </div>
      </div>
      <div className={Classes.searchHeader} id="het-aanbod">
        <Title3 as="h5">Bekijk het aanbod</Title3>
      </div>
    </>
  );
};

export default Copy;
